<template>
    <header>
        <nav
            ref="pageHeaderEl"
            class="navbar bg-white py-3 md:py-4 fixed w-full px-0"
            :class="{ 'z-50': !isPrimaryMenuOpen, 'z-20': isPrimaryMenuOpen }"
        >
            <div class="container relative flex items-center justify-between">
                <NuxtLink
                    href="/"
                    @click="setDefaultItem"
                >
                    <h1
                        class="account-name flex items-center text-lg md:text-xl font-semibold"
                        data-test="header-account-name"
                    >
                        <img
                            v-if="logo"
                            class="account-logo h-[40px] md:h-[60px] mr-2"
                            :src="logo"
                            :alt="t('common.logo_image_alt')"
                        >
                        {{ name }}
                    </h1>
                </NuxtLink>

                <div
                    v-if="isDesktop"
                    class="fulltext-search-container flex-1 max-w-md mx-4"
                >
                    <FulltextSearchContainer
                        v-model="searchQuery"
                        :show-search-results="showSearchResults"
                        :search-results="searchResults"
                        :placeholder="t('common.fulltext_search.placeholder')"
                        :accessibility-label="t('common.fulltext_search.hint')"
                        :view-all-url="viewAllUrl"
                        :view-all-text="t('common.fulltext_search.viewAll')"
                        results-container-class="absolute top-full w-full left-0 right-4 mt-1 min-w-[400px]"
                        @update:model-value="onSearchInput"
                        @search-performed="performSearch"
                        @close-results="hideResults"
                        @result-selected="onResultSelected"
                    />
                </div>

                <FulltextSearchToggle
                    v-if="!isDesktop"
                    class="ml-auto mr-2"
                    :aria-expanded="isOverlayVisible"
                    @toggle="toggleOverlay"
                />

                <div class="flex items-center gap-2 md:gap-5">
                    <div class="flex gap-5 font-semibold">
                        <NuxtLink
                            v-if="isDesktop"
                            id="subscribe"
                            class="btn btn-primary"
                            data-test="subscribe-button"
                            :href="registrationLink"
                            target="_blank"
                            rel="noopener external"
                        >
                            <span class="text-sm">{{ t('header.subscribe') }}</span>
                        </NuxtLink>
                        <button
                            v-if="!isDesktop"
                            id="user-menu"
                            data-test="user-menu"
                            class="btn btn-dropdown"
                            data-bs-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                            @click="handleUserMenuButtonClick"
                        >
                            <icon-menu-user class="icon-extra-large" />
                        </button>
                    </div>
                </div>
                <div
                    v-if="showUserDropdownMenu"
                    class="dropdown-menu user-dropdown-menu flex flex-col gap-4 rounded-md bg-white opacity-1 absolute p-6 top-[70px] md:top-[90px]"
                >
                    <NuxtLink
                        id="subscribe"
                        class="btn btn-primary"
                        data-test="subscribe-button"
                        :href="registrationLink"
                        target="_blank"
                        rel="noopener external"
                    >
                        <span class="text-sm">{{ t('header.subscribe') }}</span>
                    </NuxtLink>
                </div>
            </div>
            
            <FulltextSearchOverlay
                v-if="!isDesktop"
                v-model="searchQuery"
                :show-search-results="showSearchResults"
                :search-results="searchResults"
                :is-visible="isOverlayVisible"
                :placeholder="t('common.fulltext_search.placeholder')"
                :accessibility-label="t('common.fulltext_search.hint')"
                :view-all-url="viewAllUrl"
                :view-all-text="t('common.fulltext_search.viewAll')"
                @update:model-value="onSearchInput"
                @search-performed="performSearch"
                @result-selected="onResultSelected"
                @close-results="hideOverlay"
            />
        </nav>
    </header>
</template>

<script setup lang="ts">
    import { ref, watch, onUnmounted, onMounted } from 'vue';
    import IconMenuUser from '~/components/icons/IconMenuUser.vue';
    import FulltextSearchContainer from '~/components/fulltextSearch/FulltextSearchContainer.vue';
    import FulltextSearchOverlay from '~/components/fulltextSearch/FulltextSearchOverlay.vue';
    import FulltextSearchToggle from '~/components/fulltextSearch/FulltextSearchToggle.vue';
    import { closeModalIfClickedOutside } from '~/utils/helpers';
    import { useAccountStore } from '~/stores/accountStore';
    import { useNavigationStore } from '~/stores/navigationStore';
    import { useHeaderStore } from '~/stores/headerStore';
    import { storeToRefs } from 'pinia';
    import { useScreenSize } from '~/composables/useScreenSize';
    import { useI18n } from 'vue-i18n';
    import { useFulltextSearch } from '~/composables/useFulltextSearch';

    defineProps<{
        isPrimaryMenuOpen?: boolean;
    }>();

    const { t } = useI18n();
    const { headerEl } = storeToRefs(useHeaderStore());
    const pageHeaderEl = ref<HTMLElement | null>(null);
    const { setDefaultItem } = useNavigationStore();
    const { appColor, buttonColor, buttonTextColor, logo, name, registrationLink } = useAccountStore().state;
    const { isDesktop } = useScreenSize();
    const { 
        isOverlayVisible, 
        viewAllUrl,
        searchQuery,
        showSearchResults,
        searchResults,
        toggleOverlay,
        hideOverlay,
        hideResults,
        onSearchInput,
        performSearch,
        onResultSelected
    } = useFulltextSearch();
    
    const emit = defineEmits(
        [
            'showUserDropdownMenuChanged',
            'showStaticDropdownMenuChanged',
            'overlay-search-toggled'
        ],
    );

    const showUserDropdownMenu = ref(false);

    watch(isOverlayVisible, (newVal) => {
        if(newVal && showUserDropdownMenu.value) {
            showUserDropdownMenu.value = false;
        }
        emit('overlay-search-toggled', newVal);
    });

    const handleUserMenuButtonClick = () => {
        hideOverlay();
        showUserDropdownMenu.value = !showUserDropdownMenu.value;
        emit('showUserDropdownMenuChanged', showUserDropdownMenu.value);
    };

    const closeUserDropDownIfClickedOutside = (event: Event) => {
        closeModalIfClickedOutside(event, showUserDropdownMenu, '.user-dropdown-menu', '#user-menu');
        emit('showUserDropdownMenuChanged', showUserDropdownMenu.value);
    };

    watch(
        () => showUserDropdownMenu.value,
        showUserDropdownMenu => {
            if (showUserDropdownMenu) {
                document.addEventListener('click', closeUserDropDownIfClickedOutside);
            }
        },
    );

    onMounted(() => headerEl.value = pageHeaderEl.value);
    onUnmounted(() => {
        document.removeEventListener('click', closeUserDropDownIfClickedOutside);
    });
</script>

<style scoped lang="scss">
    $appColor: v-bind(appColor);
    $buttonColor: v-bind(buttonColor);
    $buttonTextColor: v-bind(buttonTextColor);

    .account-name {
        color: $appColor;
        line-height: 1.5rem;
    }

    .navbar {
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
        --button-color: #{$buttonColor};
    }

    .btn-primary {
        border-color: $buttonColor;
        background-color: $buttonColor;
        color: $buttonTextColor;

        &:hover {
            opacity: 0.8;
        }
    }

    .btn-dropdown {
        padding: 0;
        border: none;
    }

    .dropdown-menu {
        box-shadow: 0 1px 20px 0 rgba(0, 0, 0, 0.1);
        left: auto;
        right: 16px;
    }

    .dropdown-item {
        transition: all 0.15s ease;
        border-bottom: 1px solid;
        @apply border-neutral-100;

        &:hover {
            @apply bg-neutral-100;
        }
    }

    .dropdown-item:first-of-type {
        @apply rounded-t-md;
    }

    .dropdown-item:last-of-type {
        @apply rounded-b-md;
    }
</style>
